import { useState, useCallback, useEffect } from 'react';
import { doc, updateDoc, collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import toast from 'react-hot-toast';

const GOOGLE_CLIENT_ID = '885732677035-sn2fv620u9s4ts3b7p98gqjd6slogge4.apps.googleusercontent.com';
const SCOPES = 'https://www.googleapis.com/auth/calendar';

export function useGoogleCalendar() {
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Load Google Identity Services (replaces deprecated gapi.auth2)
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => {
      setInitialized(true);
    };
    script.onerror = () => {
      console.error('Failed to load Google Identity Services');
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const initiateAuth = useCallback(async () => {
    if (!initialized || !(window as any).google) {
      toast.error('Google Calendar not ready. Please try again.');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      const tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: SCOPES,
        callback: async (tokenResponse: any) => {
          if (tokenResponse.error) {
            console.error('Google Calendar auth error:', tokenResponse.error);
            toast.error('Failed to connect to Google Calendar');
            setLoading(false);
            return;
          }

          await updateDoc(doc(db, 'users', user.uid), {
            'calendarSettings.googleCalendar': {
              enabled: true,
              accessToken: tokenResponse.access_token,
              expiresAt: new Date(Date.now() + tokenResponse.expires_in * 1000).toISOString(),
              lastSync: new Date().toISOString(),
              syncEnabled: false,
              syncDirection: 'both',
              calendars: [],
            },
          });

          toast.success('Successfully connected to Google Calendar');
          setLoading(false);
        },
      });

      tokenClient.requestAccessToken({ prompt: 'consent' });
    } catch (error) {
      console.error('Error connecting to Google Calendar:', error);
      toast.error('Failed to connect to Google Calendar');
      setLoading(false);
    }
  }, [initialized]);

  const listCalendars = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return [];

      const userDoc = await import('firebase/firestore').then(({ getDoc }) =>
        getDoc(doc(db, 'users', user.uid))
      );
      const accessToken = userDoc.data()?.calendarSettings?.googleCalendar?.accessToken;
      if (!accessToken) return [];

      const response = await fetch(
        'https://www.googleapis.com/calendar/v3/users/me/calendarList',
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const data = await response.json();
      return data.items || [];
    } catch (error) {
      console.error('Error listing calendars:', error);
      toast.error('Failed to list calendars');
      return [];
    }
  }, []);

  const syncCalendars = useCallback(async (calendarIds: string[]) => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      const userDoc = await import('firebase/firestore').then(({ getDoc }) =>
        getDoc(doc(db, 'users', user.uid))
      );
      const accessToken = userDoc.data()?.calendarSettings?.googleCalendar?.accessToken;
      if (!accessToken) {
        toast.error('Please connect Google Calendar first');
        return;
      }

      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 3);

      const events = [];
      for (const calendarId of calendarIds) {
        const params = new URLSearchParams({
          timeMin: startDate.toISOString(),
          timeMax: endDate.toISOString(),
          singleEvents: 'true',
          orderBy: 'startTime',
        });
        const response = await fetch(
          `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?${params}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Google Calendar API error:', response.status, errorText);
          if (response.status === 401) {
            toast.error('Google Calendar access expired. Please reconnect.');
            return;
          }
          throw new Error(`Google Calendar API error: ${response.status}`);
        }
        const data = await response.json();
        events.push(...(data.items || []));
      }

      // Write synced events to Firestore so they appear in CalendarPage
      let importedCount = 0;
      for (const gcalEvent of events) {
        if (!gcalEvent.summary) continue;

        // Check if event already exists (by googleCalendarEventId)
        const existingQuery = query(
          collection(db, 'events'),
          where('googleCalendarEventId', '==', gcalEvent.id)
        );
        const existing = await getDocs(existingQuery);
        if (!existing.empty) continue; // Skip already-imported events

        await addDoc(collection(db, 'events'), {
          title: gcalEvent.summary,
          description: gcalEvent.description || '',
          start: gcalEvent.start?.dateTime || gcalEvent.start?.date || '',
          end: gcalEvent.end?.dateTime || gcalEvent.end?.date || '',
          allDay: !gcalEvent.start?.dateTime,
          location: gcalEvent.location || '',
          color: '#4285f4', // Google blue
          visibility: 'public',
          createdBy: user.uid,
          googleCalendarEventId: gcalEvent.id,
          source: 'google_calendar',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        importedCount++;
      }

      await updateDoc(doc(db, 'users', user.uid), {
        'calendarSettings.googleCalendar.lastSync': new Date().toISOString(),
      });

      toast.success(`Calendars synced: ${importedCount} new events imported`);
      return events;
    } catch (error) {
      console.error('Error syncing calendars:', error);
      toast.error('Failed to sync calendars');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    initialized,
    initiateAuth,
    listCalendars,
    syncCalendars,
  };
}
